import { FC, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material'

import { Market } from '../../../../../features/markets/types/Market'
import { Store } from '../../../../../features/store/types/Store'
import { DatePicker } from '../../../../components/DatePicker/DatePicker'
import { emptyWeightImportValues, sortMarketsByName, toWeightImportBody, toWeightImportName } from '../../helpers/importToolHelpers'
import { WeightImportFormValues } from '../../types/ImportTool'
import { ImportFileField, isCsvFile } from '../ImportFileField/ImportFileField'
import { ImportFlagField } from '../ImportFlagField/ImportFlagField'

type WeightImportFormProps = {
  stores: Store[]
  markets: Market[]
  isSubmitting?: boolean
  // Resolves true once the import was started, which clears the form.
  onSubmit: (body: FormData) => Promise<boolean>
}

const isValidDate = (date: Date | null) => !!date && !Number.isNaN(date.getTime())

export const WeightImportForm: FC<WeightImportFormProps> = ({ stores, markets, isSubmitting, onSubmit }) => {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, formState, watch } = useForm<WeightImportFormValues>({ mode: 'onChange', defaultValues: emptyWeightImportValues() })
  const sortedMarkets = useMemo(() => sortMarketsByName(markets), [markets])
  const [date, store, market] = watch(['date', 'store', 'market'])

  const submit = handleSubmit(async (values) => {
    if (await onSubmit(toWeightImportBody(values))) reset(emptyWeightImportValues())
  })

  return (
    <Stack component="form" spacing={2} onSubmit={submit} noValidate>
      <Controller
        name="base"
        control={control}
        rules={{ validate: (value) => !!value.trim() }}
        render={({ field }) => <TextField {...field} size="small" label={t('internal-analysts:import_tool_base')} required />}
      />
      <Controller
        name="store"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField {...field} select size="small" label={t('internal-analysts:import_tool_store')} required>
            {stores.map((option) => (
              <MenuItem key={option.id} value={option.name}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Controller
        name="market"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <TextField {...field} select size="small" label={t('internal-analysts:import_tool_market')} required>
            {sortedMarkets.map((option) => (
              <MenuItem key={option.id} value={option.iso}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
      <Controller
        name="date"
        control={control}
        rules={{ validate: isValidDate }}
        render={({ field }) => (
          <DatePicker
            value={field.value}
            onChange={field.onChange}
            label={t('internal-analysts:import_tool_date')}
            slotProps={{ textField: { required: true } }}
          />
        )}
      />
      <Controller
        name="test"
        control={control}
        rules={{ required: true }}
        render={({ field }) => <ImportFlagField label={t('internal-analysts:import_tool_test')} value={field.value} onChange={field.onChange} />}
      />
      <Controller
        name="updateGps"
        control={control}
        rules={{ required: true }}
        render={({ field }) => <ImportFlagField label={t('internal-analysts:import_tool_update_powerscore')} value={field.value} onChange={field.onChange} />}
      />
      <Controller
        name="file"
        control={control}
        rules={{ validate: (file) => !!file && isCsvFile(file) }}
        render={({ field }) => <ImportFileField label={t('internal-analysts:import_tool_weights_file')} value={field.value} onChange={field.onChange} />}
      />
      {isValidDate(date) && store && market && (
        <Typography variant="body2" color="text.secondary">
          {t('internal-analysts:import_tool_weights_name_preview', { name: toWeightImportName(date as Date, store, market) })}
        </Typography>
      )}
      <Stack direction="row">
        <Button type="submit" variant="contained" disabled={!formState.isValid || isSubmitting}>
          {t('internal-analysts:import_tool_upload_weights')}
        </Button>
      </Stack>
    </Stack>
  )
}
